import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { FormeoEditor, FormeoRenderer } from "formeo";
import "formeo/dist/formeo.min.css";
import intakeFormService from "@/services/intake-form/intakeFormService";
import customFieldService from "@/services/setting/customFieldService";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import { LazyFormikSelect, KeenIcon, FormikUnsavedChangesGuard } from "@/components";
import Select from "react-select";
import { createRoot } from "react-dom/client";
import { getGlobalSelectClass, sortData, stateCityMap } from "@/utils";
import { CustomFieldModal } from "@/pages/custom-fields/CustomFieldModal";
import { applyCheckboxRequiredAttributes, injectCheckboxOtherOptions } from "./checkboxOther";
import { applyInputKinds, enhanceSelectPlaceholders, getInputKinds } from "./fieldInputs";

const UNSUPPORTED_EDITOR_ELEMENT_TYPES = new Set([
    "wysiwyg",
    "richtext",
    "rich-text",
]);
const NON_REQUIRED_DISPLAY_TYPES = new Set([
    "header",
    "paragraph",
    "divider",
    "wysiwyg",
]);
const NON_REQUIRED_DISPLAY_TAGS = new Set([
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "p",
    "hr",
]);
const HEADER_TAG_OPTIONS = ["h1", "h2", "h3", "h4", "h5", "h6"];

const getFieldTypeTag = (field) => {
    const type = typeof field?.attrs?.type === "string" ? field.attrs.type.toLowerCase() : "";
    const tag = typeof field?.tag === "string" ? field.tag.toLowerCase() : "";
    return { type, tag };
};

const isUnsupportedEditorField = (field) => {
    const { type, tag } = getFieldTypeTag(field);
    return (
        UNSUPPORTED_EDITOR_ELEMENT_TYPES.has(type) ||
        UNSUPPORTED_EDITOR_ELEMENT_TYPES.has(tag)
    );
};

const isEligibleForRequiredToggle = (field) => {
    if (!field) return false;
    const { type, tag } = getFieldTypeTag(field);
    if (NON_REQUIRED_DISPLAY_TYPES.has(type)) return false;
    if (NON_REQUIRED_DISPLAY_TAGS.has(tag)) return false;
    if (isUnsupportedEditorField(field)) return false;
    return true;
};

const removeFieldIdsFromSchema = (schema, fieldIdsToRemove) => {
    if (!schema?.fields || !fieldIdsToRemove?.size) return schema;

    fieldIdsToRemove.forEach((fieldId) => {
        delete schema.fields[fieldId];
    });

    // Only drop containers this call actually emptied. A column that was already
    // empty is a deliberate part of the layout — a duplicated row waiting to be
    // filled, most obviously — and must survive.
    const columnIdsToRemove = new Set();
    if (schema.columns) {
        Object.entries(schema.columns).forEach(([columnId, column]) => {
            if (!Array.isArray(column.children) || !column.children.length) return;
            column.children = column.children.filter((id) => !fieldIdsToRemove.has(id));
            if (!column.children.length) {
                columnIdsToRemove.add(columnId);
            }
        });

        columnIdsToRemove.forEach((columnId) => {
            delete schema.columns[columnId];
        });
    }

    const rowIdsToRemove = new Set();
    if (schema.rows) {
        Object.entries(schema.rows).forEach(([rowId, row]) => {
            if (!Array.isArray(row.children) || !row.children.length) return;
            row.children = row.children.filter((id) => !columnIdsToRemove.has(id));
            if (!row.children.length) {
                rowIdsToRemove.add(rowId);
            }
        });

        rowIdsToRemove.forEach((rowId) => {
            delete schema.rows[rowId];
        });
    }

    if (schema.stages) {
        Object.values(schema.stages).forEach((stage) => {
            if (!Array.isArray(stage.children)) return;
            stage.children = stage.children.filter((id) => !rowIdsToRemove.has(id));
        });
    }

    return schema;
};

const stripUnsupportedEditorFields = (schema) => {
    if (!schema?.fields) return schema;

    const fieldIdsToRemove = new Set();
    Object.entries(schema.fields).forEach(([fieldId, field]) => {
        if (isUnsupportedEditorField(field)) {
            fieldIdsToRemove.add(fieldId);
        }
    });

    if (!fieldIdsToRemove.size) return schema;
    return removeFieldIdsFromSchema(schema, fieldIdsToRemove);
};

// Formeo ships a paragraph control seeded with a long block of filler copy. It reads
// as real content in a client-facing intake form, so swap it for a short prompt.
// The default lives in the formeo bundle, not here, so it has to be matched on output.
const FORMEO_DEFAULT_PARAGRAPH_PREFIX = "leverage agile frameworks";
const DEFAULT_PARAGRAPH_TEXT = "Add your text here";

const isDefaultParagraphContent = (content) => {
    if (typeof content !== "string") return false;

    return content
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase()
        .startsWith(FORMEO_DEFAULT_PARAGRAPH_PREFIX);
};

const sanitizeFormeoSchema = (schema) => {
    if (!schema) return schema;
    stripUnsupportedEditorFields(schema);

    if (schema.fields) {
        Object.values(schema.fields).forEach((f) => {
            f.attrs ??= {};
            f.attrs.name ??= "";
            f.attrs.placeholder ??= "";
            f.attrs.value ??= "";
            f.attrs.className ??= "";
            const { type, tag } = getFieldTypeTag(f);

            if (tag === "p" && isDefaultParagraphContent(f.content)) {
                f.content = DEFAULT_PARAGRAPH_TEXT;
            }

            const isHeaderElement =
                type === "header" || HEADER_TAG_OPTIONS.includes(tag);
            if (isHeaderElement) {
                const styleString = typeof f.attrs.style === "string" ? f.attrs.style.trim() : "";
                const hasFontWeight = /(^|;)\s*font-weight\s*:/.test(styleString);
                if (!hasFontWeight) {
                    f.attrs.style = styleString
                        ? `${styleString.replace(/;?\s*$/, ";")} font-weight: 700`
                        : "font-weight: 700";
                }
            }
            if (typeof f.attrs.type !== "string" || !f.attrs.type) {
                if (tag === "select") {
                    f.attrs.type = "select";
                } else if (tag === "textarea") {
                    f.attrs.type = "textarea";
                } else if (tag === "checkbox") {
                    f.attrs.type = "checkbox";
                } else if (tag === "radio") {
                    f.attrs.type = "radio";
                } else if (tag === "input") {
                    f.attrs.type = "text";
                }
            }

            f.config ??= {};
            f.config.label ??= "";
            f.config.category ??= "";

            if (Array.isArray(f.options)) {
                f.options.forEach((o) => {
                    o.label ??= "";
                    o.value ??= "";
                });
            }
        });
    }

    if (schema.rows) {
        Object.values(schema.rows).forEach((r) => {
            r.className = Array.isArray(r.className) ? r.className : [];
            r.config ??= {};
            r.config.legend ??= "";
        });
    }

    if (schema.columns) {
        Object.values(schema.columns).forEach((c) => {
            c.className = Array.isArray(c.className) ? c.className : [];
            c.config ??= {};
            c.config.width ??= "100%";
        });
    }

    if (schema.stages) {
        Object.values(schema.stages).forEach((stage) => {
            stage.conditions = Array.isArray(stage.conditions)
                ? stage.conditions.filter(
                    (cond) =>
                        cond.if?.every((i) => typeof i.source === "string") &&
                        cond.then?.every((t) => typeof t.target === "string")
                )
                : [];
        });
    }

    return schema;
};

const isCheckboxField = (field) => {
    const type = field?.attrs?.type;
    return type === "checkbox" || field?.tag === "checkbox";
};

const createCheckboxOtherState = () => ({
    byName: new Set(),
    byId: new Set(),
});

const getCheckboxOtherStateFromSchema = (schema) => {
    const state = createCheckboxOtherState();
    if (!schema?.fields) return state;

    Object.entries(schema.fields).forEach(([fieldId, field]) => {
        if (!isCheckboxField(field)) return;
        if (!field?.config?.otherOption) return;
        const name =
            field?.attrs?.name ||
            field?.config?.sys?.full_name;
        if (name) state.byName.add(name);
        if (fieldId) state.byId.add(fieldId);
    });

    return state;
};

const applyCheckboxOtherToSchema = (schema, otherState) => {
    if (!schema?.fields) return schema;

    Object.entries(schema.fields).forEach(([fieldId, field]) => {
        if (!isCheckboxField(field)) return;

        const name =
            field?.attrs?.name ||
            field?.config?.sys?.full_name;

        const hasOther =
            (fieldId && otherState.byId.has(fieldId)) ||
            (name && otherState.byName.has(name));

        field.config ??= {};

        if (hasOther) {
            field.config.otherOption = true;
        } else {
            // 🔥 THIS WAS MISSING
            delete field.config.otherOption;
        }
    });

    return schema;
};


const getFieldIdFromElement = (fieldEl) => {
    return (
        fieldEl?.dataset?.fieldId ||
        fieldEl?.dataset?.id ||
        fieldEl?.getAttribute("data-field-id") ||
        fieldEl?.getAttribute("data-id") ||
        fieldEl?.getAttribute("data-uid") ||
        fieldEl?.getAttribute("data-uuid") ||
        fieldEl?.id
    );
};

const getCheckboxInfoFromElement = (fieldEl, editorInstance) => {
    const formeoField = fieldEl.closest(".formeo-field");
    if (!formeoField) return null;

    const fieldId =
        formeoField.dataset.fieldId ||
        formeoField.id;

    const fieldFromSchema =
        editorInstance?.formData?.fields?.[fieldId];

    if (!fieldFromSchema) return null;
    if (!isCheckboxField(fieldFromSchema)) return null;

    const name =
        fieldFromSchema?.attrs?.name ||
        fieldFromSchema?.config?.sys?.full_name ||
        "";

    return { fieldId, name };
};

const ensureCheckboxOtherPreview = (fieldEl, name) => {
    if (fieldEl.querySelector(`[data-other-preview="${name}"]`)) return;

    const wrapper = document.createElement("div");
    wrapper.dataset.otherPreview = name;
    wrapper.className = "mt-1 flex items-center gap-2";

    const label = document.createElement("span");
    label.className = "text-sm";
    label.textContent = "Other:";

    const input = document.createElement("input");
    input.type = "text";
    input.placeholder = "Other";
    input.className = "input input-sm";
    input.disabled = true;

    wrapper.append(label, input);
    const preview =
        fieldEl.querySelector(".field-preview") || fieldEl;

    preview.appendChild(wrapper);
};

const removeCheckboxOtherPreview = (fieldEl, name) => {
    const preview = fieldEl.querySelector(`[data-other-preview="${name}"]`);
    if (preview) preview.remove();
};

const isOtherEnhanced = (fieldEl) =>
    fieldEl.dataset.otherEnhanced === "true";

const markOtherEnhanced = (fieldEl) => {
    fieldEl.dataset.otherEnhanced = "true";
};

const getFieldActionControlsWrap = (fieldEl) => {
    if (!fieldEl) return null;

    let wrap = fieldEl.querySelector('[data-field-action-controls="true"]');
    if (wrap) return wrap;

    wrap = document.createElement("div");
    wrap.dataset.fieldActionControls = "true";
    wrap.className = "w-full mt-1 flex items-center gap-2 flex-wrap";

    const previewEl = fieldEl.querySelector(".field-preview");
    if (previewEl && previewEl.parentNode === fieldEl) {
        previewEl.insertAdjacentElement("afterend", wrap);
    } else {
        fieldEl.appendChild(wrap);
    }

    return wrap;
};

const syncFieldActionControlsVisibility = (fieldEl) => {
    const wrap = fieldEl?.querySelector('[data-field-action-controls="true"]');
    if (!wrap) return;

    const hasVisibleButton = Array.from(wrap.children).some((child) => child.style.display !== "none");
    wrap.style.display = hasVisibleButton ? "flex" : "none";
};

const enhanceCheckboxOtherControls = (editorRoot, otherState, editorInstance) => {
    if (!editorRoot || !editorInstance?.formData?.fields) return;

    const fields = editorRoot.querySelectorAll(".formeo-field");

    fields.forEach((fieldEl) => {
        if (fieldEl.closest(".formeo-controls")) return;
        if (isOtherEnhanced(fieldEl)) return; // 🛑 CRITICAL GUARD

        const checkboxInfo = getCheckboxInfoFromElement(fieldEl, editorInstance);
        if (!checkboxInfo?.name) return;

        const { fieldId, name } = checkboxInfo;

        let toggleBtn = document.createElement("button");
        toggleBtn.type = "button";
        toggleBtn.dataset.toggleOther = "true";


        const sync = () => {
            const active =
                otherState.byName.has(name) ||
                (fieldId && otherState.byId.has(fieldId));
            toggleBtn.className = active ? "!btn !btn-link-danger" : "mt-1 text-xs !btn !btn-link";
            toggleBtn.textContent = active ? "Remove Other" : "Add Other";

            if (active) {
                ensureCheckboxOtherPreview(fieldEl, name);
            } else {
                removeCheckboxOtherPreview(fieldEl, name);
            }

            syncFieldActionControlsVisibility(fieldEl);
        };

        toggleBtn.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();

            const active =
                otherState.byName.has(name) ||
                (fieldId && otherState.byId.has(fieldId));

            if (active) {
                otherState.byName.delete(name);
                if (fieldId) otherState.byId.delete(fieldId);
            } else {
                otherState.byName.add(name);
                if (fieldId) otherState.byId.add(fieldId);
            }

            sync();
        };

        const controlsWrap = getFieldActionControlsWrap(fieldEl);
        controlsWrap?.appendChild(toggleBtn);
        markOtherEnhanced(fieldEl);
        sync();
        syncFieldActionControlsVisibility(fieldEl);
    });
};

const buildSelectOptionsSignature = (options) =>
    options.map((o) => `${o.value}::${o.label}`).join("|");

const buildFieldPlaceholderMap = (schema) => {
    const map = {};
    const fields = Array.isArray(schema?.fields) ? schema.fields : Object.values(schema?.fields || {});

    fields.forEach((field) => {
        const name =
            field?.attrs?.name ||
            field?.config?.sys?.full_name;

        if (!name) return;

        // No label fallback here either — otherwise it would put the label back into
        // the control that the two placeholder defaults above deliberately leave out.
        map[name] =
            field?.attrs?.placeholder ||
            field?.config?.placeholder ||
            "";
    });

    return map;
};

const enhanceMultiSelects = (
    container,
    {
        selector = ".field-preview select[multiple]",
        isDisabled = true,
        value,
        placeholderMap = {},
    } = {}
) => {
    if (!container) return;

    const selects = container.querySelectorAll(selector);

    selects.forEach((select) => {
        const placeholder =
            placeholderMap[select.name] ||
            select.getAttribute("placeholder") ||
            select.dataset.placeholder ||
            "Select options...";
        const options = Array.from(select.options).map((opt) => ({
            label: opt.text,
            value: opt.value,
        }));
        const optionsSig = buildSelectOptionsSignature(options);

        const prevSig = select.dataset.optionsSignature;
        const isEnhanced = select.dataset.enhanced === "true";

        if (isEnhanced && prevSig !== optionsSig) {
            const reactSelectId = select.dataset.reactSelectId;
            if (reactSelectId) {
                const mount = select.parentNode?.querySelector(
                    `[data-react-select-for="${reactSelectId}"]`
                );
                if (mount) mount.remove();
            }
            delete select.dataset.enhanced;
        }

        if (select.dataset.enhanced === "true") return;

        select.dataset.enhanced = "true";
        select.dataset.optionsSignature = optionsSig;

        const reactSelectId =
            select.dataset.reactSelectId ||
            `rs_${Math.random().toString(36).slice(2, 9)}`;
        select.dataset.reactSelectId = reactSelectId;

        const mount = document.createElement("div");
        mount.className = "react-select-wrapper";
        mount.dataset.reactSelectFor = reactSelectId;

        select.classList.add("hidden-native-select");
        select.required = false;
        select.removeAttribute("required");
        select.tabIndex = -1;
        select.setAttribute("aria-hidden", "true");

        select.parentNode.insertBefore(mount, select.nextSibling);

        // Ensure builder preview doesn't preselect any option
        Array.from(select.options).forEach((opt) => {
            opt.selected = false;
        });
        select.selectedIndex = -1;

        const root = createRoot(mount);

        const selectProps = {
            classNames: getGlobalSelectClass(),
            isMulti: true,
            isDisabled,
            options,
            className: "input",
            classNamePrefix: "react-select",
            placeholder,
            onChange: (selected) => {
                const values = (selected || []).map((s) => s.value);

                Array.from(select.options).forEach((opt) => {
                    opt.selected = values.includes(opt.value);
                });

                select.dispatchEvent(new Event("change", { bubbles: true }));
            },
        };

        if (value !== undefined) {
            selectProps.value = value;
        }

        root.render(
            <Select
                {...selectProps}
            />
        );
    });
};

const getFormeoControl = (input) => {
    return input?.closest(".formeo-control") || input?.parentElement;
};

const enhancePreviewCityStateFields = (container) => {
    const stateInput = container.querySelector('input[name="contact.state"]');
    const cityInput = container.querySelector('input[name="contact.city"]');

    if (!stateInput) return;
    if (stateInput.dataset.previewEnhanced === "true") return;

    stateInput.dataset.previewEnhanced = "true";
    if (cityInput) cityInput.dataset.previewEnhanced = "true";

    const stateControl = getFormeoControl(stateInput);
    const cityControl = cityInput ? getFormeoControl(cityInput) : null;
    if (!stateControl) return;

    [stateInput, cityInput].filter(Boolean).forEach((input) => {
        input.classList.add("hidden-native-input");
        input.setAttribute("aria-hidden", "true");
        input.tabIndex = -1;
    });

    const stateMount = document.createElement("div");
    const cityMount = document.createElement("div");

    stateMount.className = "react-select-wrapper mt-1";
    cityMount.className = "react-select-wrapper mt-1";

    stateControl.appendChild(stateMount);
    if (cityControl) cityControl.appendChild(cityMount);

    let stateValue = stateInput.value || "";
    let cityValue = cityInput?.value || "";

    const stateOptions = sortData(
        Object.keys(stateCityMap).map((stateName) => ({
            label: stateName,
            value: stateName,
        })),
        { mode: "alpha", key: "label" }
    );

    const sync = (input, value) => {
        if (!input) return;
        input.value = value || "";
        input.dispatchEvent(new Event("input", { bubbles: true }));
        input.dispatchEvent(new Event("change", { bubbles: true }));
    };

    const stateRoot = createRoot(stateMount);
    const cityRoot = createRoot(cityMount);

    const renderCityInput = () => {
        if (!cityInput) return;
        cityRoot.render(
            <input
                type="text"
                className="input"
                name="contact.city"
                placeholder="Enter City"
                value={cityValue}
                onChange={(e) => {
                    cityValue = e.target.value;
                    sync(cityInput, cityValue);
                    renderCityInput();
                }}
            />
        );
    };

    const renderStateSelect = () => {
        stateRoot.render(
            <Select
                classNames={getGlobalSelectClass()}
                options={stateOptions}
                placeholder="Select State"
                value={stateValue ? { label: stateValue, value: stateValue } : null}
                onChange={(opt) => {
                    stateValue = opt?.value || "";
                    sync(stateInput, stateValue);
                    if (cityInput) {
                        cityValue = "";
                        sync(cityInput, "");
                        renderCityInput();
                    }
                    renderStateSelect();
                }}
                className="input"
                classNamePrefix="react-select"
            />
        );
    };

    renderStateSelect();
    if (cityInput) renderCityInput();
};

const enhanceRenderedSingleCheckboxPreview = (container) => {
    if (!container) return;

    const otherRows = container.querySelectorAll(".formeo-other-option[data-other-for]");

    otherRows.forEach((otherRow) => {
        const parent = otherRow.parentElement;
        if (!parent) return;

        const directCheckboxes = Array.from(
            parent.querySelectorAll(':scope > input[type="checkbox"]')
        );

        if (directCheckboxes.length !== 1) return;

        const checkbox = directCheckboxes[0];
        const label =
            parent.querySelector(`:scope > label[for="${CSS.escape(checkbox.id)}"]`) ||
            checkbox.nextElementSibling;

        if (!(label instanceof HTMLLabelElement)) return;

        let pairWrap = parent.querySelector('[data-single-checkbox-pair="true"]');
        if (!pairWrap) {
            pairWrap = document.createElement("div");
            pairWrap.dataset.singleCheckboxPair = "true";
            pairWrap.style.setProperty("display", "inline-flex");
            pairWrap.style.setProperty("align-items", "center");
            pairWrap.style.setProperty("gap", "0.5rem");
            pairWrap.style.setProperty("flex-wrap", "wrap");
            parent.insertBefore(pairWrap, checkbox);
        }

        if (checkbox.parentElement !== pairWrap) {
            pairWrap.appendChild(checkbox);
        }

        if (label.parentElement !== pairWrap) {
            pairWrap.appendChild(label);
        }

        parent.style.setProperty("display", "flex");
        parent.style.setProperty("flex-direction", "column");
        parent.style.setProperty("align-items", "flex-start");
        parent.style.setProperty("gap", "0.5rem");

        otherRow.style.setProperty("margin-top", "0");
    });
};

const enhanceCheckboxPreviewLayout = (editorRoot) => {
    if (!editorRoot) return;

    const fields = editorRoot.querySelectorAll(".formeo-field");
    const checkboxParentLists = new Set();

    fields.forEach((fieldEl) => {
        const previewEl = fieldEl.querySelector(".field-preview");
        const checkboxInputs = previewEl?.querySelectorAll('input[type="checkbox"]');
        const checkboxOptionRows = previewEl?.querySelectorAll(".f-checkbox");
        const isSingleCheckbox =
            Boolean(previewEl?.querySelector(':scope > input[type="checkbox"]')) ||
            checkboxOptionRows?.length === 1;
        const shouldApplyLayout =
            Boolean(previewEl) &&
            Boolean(checkboxInputs?.length) &&
            isSingleCheckbox;

        const labelEl = fieldEl.querySelector(".prev-label");
        const controlsWrap = fieldEl.querySelector('[data-field-action-controls="true"]');
        if (!shouldApplyLayout) return;

        const parentList = fieldEl.parentElement;
        if (parentList?.classList.contains("children")) {
            checkboxParentLists.add(parentList);
        }

        fieldEl.style.setProperty("display", "flex");
        fieldEl.style.setProperty("flex-wrap", "wrap");
        fieldEl.style.setProperty("align-items", "center");
        fieldEl.style.setProperty("gap", "0.5rem");

        if (labelEl) {
            labelEl.style.setProperty("display", "inline-flex");
            // labelEl.style.setProperty("align-items", "start");
        }

        let contentWrap = fieldEl.querySelector('[data-checkbox-content-wrap="true"]');
        const otherPreview = previewEl?.querySelector("[data-other-preview]");
        const singleCheckboxInput =
            previewEl?.querySelector(':scope > input[type="checkbox"]') ||
            previewEl?.querySelector('input[type="checkbox"]');

        if (otherPreview && labelEl && previewEl && singleCheckboxInput) {
            if (!contentWrap) {
                contentWrap = document.createElement("div");
                contentWrap.dataset.checkboxContentWrap = "true";
                contentWrap.style.setProperty("justify-content", "start");
                contentWrap.style.setProperty("flex-direction", "row-reverse");
                contentWrap.style.setProperty("width", "100%");
                contentWrap.style.setProperty("display", "inline-flex");
                contentWrap.style.setProperty("align-items", "center");
                contentWrap.style.setProperty("gap", "0.5rem");
                contentWrap.style.setProperty("order", "-2");

                fieldEl.insertBefore(contentWrap, labelEl);
            }

            if (labelEl.parentElement !== contentWrap) {
                contentWrap.appendChild(labelEl);
            }
            if (singleCheckboxInput.parentElement !== contentWrap) {
                contentWrap.appendChild(singleCheckboxInput);
            }

            previewEl.style.setProperty("display", "flex");
            previewEl.style.setProperty("flex-direction", "column");
            previewEl.style.setProperty("align-items", "flex-start");
            previewEl.style.setProperty("gap", "0.25rem");
            previewEl.style.removeProperty("order");
        } else {
            if (contentWrap) {
                const parent = contentWrap.parentElement;
                if (parent) {
                    if (labelEl && labelEl.parentElement === contentWrap) {
                        parent.insertBefore(labelEl, contentWrap);
                    }
                    if (singleCheckboxInput && singleCheckboxInput.parentElement === contentWrap && previewEl) {
                        previewEl.insertBefore(singleCheckboxInput, previewEl.firstChild);
                    }
                }
                contentWrap.remove();
            }

            if (previewEl) {
                previewEl.style.setProperty("display", "inline-flex");
                previewEl.style.setProperty("flex-direction", "column");
                previewEl.style.setProperty("align-items", "flex-start");
                previewEl.style.setProperty("gap", "0.25rem");
                previewEl.style.setProperty("order", "-2");
            }
        }

        if (previewEl) {
            previewEl.style.setProperty("max-width", "100%");
        }

        if (otherPreview) {
            otherPreview.style.setProperty("display", "flex");
            otherPreview.style.setProperty("align-items", "center");
            otherPreview.style.setProperty("gap", "0.5rem");
            otherPreview.style.setProperty("margin-top", "0.25rem");
            otherPreview.style.setProperty("max-width", "100%");
        }

        if (controlsWrap) {
            controlsWrap.style.setProperty("display", "flex");
            controlsWrap.style.setProperty("align-items", "center");
            controlsWrap.style.setProperty("gap", "0.5rem");
        }
    });

    checkboxParentLists.forEach((parentList) => {
        parentList.style.setProperty("display", "flex");
        parentList.style.setProperty("flex-direction", "column");
    });
};


const getFieldIcon = (field) => {
    const type = field.attrs?.type;
    if (!type) return field.tag;

    const map = {
        text: 'text-input',
        date: 'calendar',
        email: 'email',
        file: 'upload',
        url: 'text-input',
        checkbox: 'checkbox',
        radio: 'radio-group',
        number: 'hash',
        tel: 'phone-receiver',
    };

    return map[type] ?? field.tag;
};

const REQUIRED_LOCKED_SYS_FIELDS = new Set([
    "contact.contact_type_id",
    "contact.first_name",
    "contact.last_name",
]);

const normalizeClassName = (className) => {
    if (typeof className !== "string") return "";
    return className.trim().replace(/\s+/g, " ");
};

const setRequiredClassName = (className, isRequired) => {
    const tokens = new Set(
        normalizeClassName(className).split(/\s+/).filter(Boolean)
    );
    if (isRequired) {
        tokens.add("required-field");
    } else {
        tokens.delete("required-field");
    }
    return Array.from(tokens).join(" ");
};

const isRequiredField = (field) => {
    if (field?.attrs?.required) return true;
    if (typeof field?.attrs?.className === "string") {
        return field.attrs.className.split(/\s+/).includes("required-field");
    }
    if (field?.config?.required) return true;
    if (field?.config?.validation?.required) return true;
    return false;
};

const setFieldRequired = (field, isRequired) => {
    if (!field) return;
    field.attrs ??= {};
    field.config ??= {};
    field.config.validation ??= {};

    if (isRequired) {
        field.attrs.required = true;
        field.config.required = true;
        field.config.validation.required = true;
    } else {
        delete field.attrs.required;
        delete field.config.required;
        delete field.config.validation.required;
    }

    field.attrs.className = setRequiredClassName(field.attrs.className, isRequired);
};

const isLockedRequiredField = (field) =>
    Boolean(field?.config?.sys?.full_name) &&
    REQUIRED_LOCKED_SYS_FIELDS.has(field.config.sys.full_name);

const enforceLockedRequiredFields = (schema) => {
    if (!schema?.fields) return schema;
    Object.values(schema.fields).forEach((field) => {
        if (isLockedRequiredField(field)) {
            setFieldRequired(field, true);
        }
    });
    return schema;
};

const updateControlPanelRequiredIndicator = (field, isRequired) => {
    const fullName = field?.config?.sys?.full_name || field?.attrs?.name;
    if (!fullName) return;

    const controls = document.querySelectorAll(".formeo-controls .field-control");
    controls.forEach((control) => {
        const sysClass = Array.from(control.classList)
            .find((cls) => cls.startsWith("sys_") && cls.endsWith("-control"));
        if (!sysClass) return;

        const raw = sysClass.replace("sys_", "").replace("-control", "");
        const firstUnderscoreIndex = raw.indexOf("_");
        if (firstUnderscoreIndex === -1) return;

        const normalized =
            raw.slice(0, firstUnderscoreIndex) +
            "." +
            raw.slice(firstUnderscoreIndex + 1);

        if (normalized !== fullName) return;

        if (isRequired) {
            control.classList.add("required-field");
        } else {
            control.classList.remove("required-field");
        }
    });
};

const updatePreviewRequiredIndicator = (fieldEl, isRequired) => {
    const labelWrap = fieldEl.querySelector(".prev-label");
    if (labelWrap) {
        const existingStars = Array.from(
            labelWrap.querySelectorAll(".required-indicator, .text-error")
        ).filter((el) => (el.textContent || "").trim() === "*");

        if (isRequired) {
            if (!existingStars.length) {
                const star = document.createElement("span");
                star.className = "text-error required-indicator";
                star.textContent = "*";
                labelWrap.appendChild(star);
            } else {
                existingStars.slice(1).forEach((el) => el.remove());
                existingStars[0].classList.add("required-indicator");
            }
        } else {
            existingStars.forEach((el) => el.remove());
        }
    }

    const previewInput = fieldEl.querySelector(".field-preview [name]");
    if (previewInput) {
        if (isRequired) {
            previewInput.setAttribute("required", "");
            previewInput.classList.add("required-field");
        } else {
            previewInput.removeAttribute("required");
            previewInput.classList.remove("required-field");
        }
    }
};

const disablePreviewInputs = (fieldEl, field) => {
    const fullName = field?.config?.sys?.full_name || field?.attrs?.name || "";
    if (fullName === "contact.contact_type_id") return;
    const previewInputs = fieldEl.querySelectorAll(".field-preview input, .field-preview select, .field-preview textarea");
    previewInputs.forEach((el) => {
        el.setAttribute("disabled", "");
    });
};

const mapSystemFieldsToFormeoControls = (data) => {
    const controls = [];

    const createControl = (field, module) => {
        if (!field || !field.tag || !field.attrs || !field.config) {
            console.error("Invalid field data:", field);
            return null;
        }
        if (!field.config.label) {
            field.config.label = field.attrs?.name || "Field";
        }

        const originalName = field.attrs.name || "field";
        const category = field.config.category || module;
        const normalizedCategory = category.replace(/\./g, "_");  // Normalize category

        const fullName = [module, originalName].join(".");
        const requiredFlag =
            field.attrs?.required ??
            field.config?.required ??
            field.config?.validation?.required ??
            REQUIRED_LOCKED_SYS_FIELDS.has(fullName);
        const isRequired = requiredFlag ? "required-field" : "";
        const controlId = `${isRequired} sys_${module}_${originalName}`;
        const icon = getFieldIcon(field);
        const isFileField = field?.attrs?.type === "file";
        // const icon = field.tag === "input" ? "text-input" : field.tag;
        console.log('icon>>>', icon)
        console.log('isRequired', field.attrs)

        if (data.meta) {
            let options = [];
            const isMultiple = Boolean(field.attrs?.multiple);

            // Contact Type (system field)
            if (
                field.attrs?.name === 'contact_type_id' &&
                data.meta.contact_type_options
            ) {
                options = data.meta.contact_type_options;
            }

            // Contact custom fields
            else if (
                field.config.category?.startsWith('contact.custom') &&
                data.meta.custom_field_options?.contact
            ) {
                const label = field.config.label;
                options = data.meta.custom_field_options.contact[label] || [];
            }

            // Matter custom fields
            else if (
                field.config.category?.startsWith('matter.custom') &&
                data.meta.custom_field_options?.matter
            ) {
                const label = field.config.label;
                options = data.meta.custom_field_options.matter[label] || [];
            }

            if (options.length) {
                if (field.tag === 'select') {
                    const control = {
                        tag: 'select',
                        attrs: {
                            ...field.attrs,
                            name: fullName,
                            className: isRequired,
                            required: Boolean(requiredFlag),
                            // Don't fall back to the label — the field already shows it,
                            // so repeating it inside the control just reads as redundant.
                            // A select still needs *some* empty-state prompt, hence the
                            // neutral default rather than a blank one.
                            placeholder: field.attrs.placeholder || 'Select an option',
                            ...(isMultiple ? { multiple: true } : {}),
                        },
                        config: {
                            ...field.config,
                            required: Boolean(requiredFlag),
                            validation: {
                                ...field.config?.validation,
                                required: Boolean(requiredFlag),
                            },
                            sys: {
                                module,
                                original_name: originalName,
                                full_name: fullName,
                            },
                        },
                        meta: {
                            id: controlId,
                            group: module,
                            icon: 'select',
                        },
                        options: options.map(opt => ({
                            label: opt.label,
                            value: opt.value,
                        })),
                    };
                    return control;
                }

                if (field?.attrs?.type === "radio") {
                    return {
                        tag: "input",
                        attrs: {
                            type: "radio",
                            name: fullName,
                            className: isRequired,
                            required: Boolean(requiredFlag),
                            disabled: true,
                        },
                        config: {
                            ...field.config,
                            required: Boolean(requiredFlag),
                            validation: {
                                ...field.config?.validation,
                                required: Boolean(requiredFlag),
                            },
                            sys: {
                                module,
                                original_name: originalName,
                                full_name: fullName,
                            },
                        },
                        meta: {
                            id: controlId,
                            group: module,
                            icon: 'radio-group',
                        },
                        options: options.map(opt => ({
                            label: opt.label ?? "",
                            value: opt.value ?? "",
                        })),
                    };
                }

                if (isCheckboxField(field) && isMultiple) {
                    const control = {
                        tag: "input",
                        attrs: {
                            type: "checkbox",
                            name: fullName,
                            className: isRequired,
                            required: Boolean(requiredFlag),
                            disabled: true,
                        },
                        config: {
                            ...field.config,
                            required: Boolean(requiredFlag),
                            validation: {
                                ...field.config?.validation,
                                required: Boolean(requiredFlag),
                            },
                            sys: {
                                module,
                                original_name: originalName,
                                full_name: fullName,
                            },
                        },
                        meta: {
                            id: controlId,
                            group: module,
                            icon: 'checkbox',
                        },
                        options: options.map(opt => ({
                            label: opt.label ?? "",
                            value: opt.value ?? "",
                        })),
                    };
                    return control;
                }
            }
        }
        return {
            tag: field.tag,
            attrs: {
                ...field.attrs,
                name: fullName,
                className: isRequired,
                required: Boolean(requiredFlag),
                // Leave blank when the custom field has no placeholder of its own —
                // echoing the label inside the input is redundant with the label above it.
                placeholder: field.attrs.placeholder || '',
                ...(isFileField ? { multiple: true } : {}),
                disabled: true, // always disabled when creating/editing
            },
            config: {
                ...field.config,
                required: Boolean(requiredFlag),
                validation: {
                    ...field.config?.validation,
                    required: Boolean(requiredFlag),
                },
                sys: {
                    module,
                    original_name: originalName,
                    full_name: fullName,
                },
            },
            meta: {
                id: controlId,
                group: `${module}`,
                icon: icon,
                className: ''
            },
        };
    };

    if (data.contact) {
        Object.values(data.contact).forEach((field) => {
            if (Array.isArray(field)) {
                field.forEach((subField) => {
                    const control = createControl(subField, "contact");
                    if (control) controls.push(control);
                });
            } else {
                const control = createControl(field, "contact");
                if (control) controls.push(control);
            }
        });
    }

    if (data.matter) {
        Object.values(data.matter).forEach((field) => {
            if (Array.isArray(field)) {
                field.forEach((subField) => {
                    const control = createControl(subField, "matter");
                    if (control) controls.push(control);
                });
            } else {
                const control = createControl(field, "matter");
                if (control) controls.push(control);
            }
        });
    }

    return controls;
};

const isRequiredControl = (control) => {
    if (control?.attrs?.required) return true;
    if (typeof control?.attrs?.className === "string") {
        return control.attrs.className.split(/\s+/).includes("required-field");
    }
    if (typeof control?.meta?.id === "string") {
        return control.meta.id.split(/\s+/).includes("required-field");
    }
    if (control?.config?.required) return true;
    if (control?.config?.validation?.required) return true;
    return false;
};

const stripDisabledFromSchema = (schema) => {
    if (!schema?.fields) return schema;

    Object.values(schema.fields).forEach((f) => {
        if (f?.attrs?.disabled) {
            delete f.attrs.disabled;
        }
    });

    return schema;
};

const disableSystemFieldsForBuilder = (schema) => {
    if (!schema?.fields) return schema;

    Object.values(schema.fields).forEach((field) => {
        if (field?.config?.sys) {
            const isSelectField =
                field?.tag === "select" ||
                field?.attrs?.type === "select";
            if (isSelectField) return;
            field.attrs ??= {};
            field.attrs.disabled = true;
        }
    });

    return schema;
};

// Formeo ids are the first 8 characters of a uuid.
const createFormeoId = () => {
    const generated = globalThis.crypto?.randomUUID?.();
    return generated ? generated.slice(0, 8) : Math.random().toString(36).slice(2, 10);
};

const cloneValue = (value) => JSON.parse(JSON.stringify(value));

// Copies a row's layout — its columns, their widths and the row's own settings —
// directly into the schema, then leaves it to the caller to reload the editor.
//
// Any field carrying a name is left behind. Contact and matter fields each map
// to a single record field, which is why the builder greys a control out once
// it has been used; and the public form posts its answers keyed by field name
// (see intakeFormSubmit.js), so two fields sharing one name would silently
// overwrite each other. Headings and paragraphs have no name and no such limit,
// so they are copied along with the layout.
const duplicateRowInSchema = (schema, rowId) => {
    const sourceRow = schema?.rows?.[rowId];
    if (!sourceRow) return null;

    const stage = Object.values(schema.stages || {}).find(
        (candidate) => Array.isArray(candidate.children) && candidate.children.includes(rowId)
    );
    if (!stage) return null;

    let skippedNamedFields = 0;
    const newRowId = createFormeoId();
    const newRow = { ...cloneValue(sourceRow), id: newRowId, children: [] };

    (sourceRow.children || []).forEach((columnId) => {
        const sourceColumn = schema.columns?.[columnId];
        if (!sourceColumn) return;

        const newColumnId = createFormeoId();
        const newColumn = { ...cloneValue(sourceColumn), id: newColumnId, children: [] };

        (sourceColumn.children || []).forEach((fieldId) => {
            const sourceField = schema.fields?.[fieldId];
            if (!sourceField) return;

            const fieldName =
                sourceField?.config?.sys?.full_name || sourceField?.attrs?.name || "";
            if (fieldName) {
                skippedNamedFields += 1;
                return;
            }

            const newFieldId = createFormeoId();
            schema.fields[newFieldId] = { ...cloneValue(sourceField), id: newFieldId };
            newColumn.children.push(newFieldId);
        });

        schema.columns[newColumnId] = newColumn;
        newRow.children.push(newColumnId);
    });

    schema.rows[newRowId] = newRow;
    stage.children.splice(stage.children.indexOf(rowId) + 1, 0, newRowId);

    return { newRowId, skippedNamedFields };
};

// Key order in the schema shifts as rows and fields are added or removed, so a
// plain JSON.stringify would report changes that aren't there.
const stableStringify = (value) => {
    if (Array.isArray(value)) {
        return `[${value.map(stableStringify).join(",")}]`;
    }
    if (value && typeof value === "object") {
        return `{${Object.keys(value)
            .sort()
            .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
            .join(",")}}`;
    }
    return JSON.stringify(value) ?? "null";
};

const buildControlOptions = (controls) => {
    const normalizedControls = controls.map((control) => {
        const metaId =
            typeof control?.meta?.id === "string"
                ? control.meta.id.trim().replace(/\s+/g, " ")
                : control?.meta?.id;
        const className =
            typeof control?.attrs?.className === "string"
                ? control.attrs.className.trim().replace(/\s+/g, " ")
                : control?.attrs?.className;


        return {
            ...control,
            attrs: {
                ...control.attrs,
                className,
            },
            meta: {
                ...control.meta,
                id: metaId,
            },
        };
    });

    const byRequiredThenLabel = (a, b) => {
        const aReq = isRequiredControl(a);
        const bReq = isRequiredControl(b);
        if (aReq !== bReq) return aReq ? -1 : 1;
        return (a?.config?.label || "").localeCompare(b?.config?.label || "");
    };

    const contact = normalizedControls
        .filter((c) => c.meta.group === "contact")
        .sort(byRequiredThenLabel);
    const matter = normalizedControls
        .filter((c) => c.meta.group === "matter")
        .sort(byRequiredThenLabel);
    const contactOrder = contact.map((c) => c.meta.id);
    const matterOrder = matter.map((c) => c.meta.id);
    const orderedElements = [
        ...contact,
        ...matter,
        ...normalizedControls.filter(
            (c) => c.meta.group !== "contact" && c.meta.group !== "matter"
        ),
    ];
    return {
        elements: orderedElements,
        sortable: false,
        disable: {
            // groups: ["common", "html", "layout"],
            // groups: ["html", "layout"],
            groups: ['common'],
            elements: ['divider', 'wysiwyg', 'tinymce', 'richtext', 'rich-text'],
        },
        elementOrder: {
            contact: contactOrder,
            matter: matterOrder,
        },
        groups: [
            { id: "contact", label: "Contact Fields", elementOrder: contactOrder },
            { id: "matter", label: "Matter Fields", elementOrder: matterOrder },
        ],
        groupOrder: ["contact", "matter"],
    };
};

const clearFormeoControlOrder = () => {
    if (typeof window === "undefined" || !window.localStorage) return;
    Object.keys(window.localStorage)
        .filter((key) => key.startsWith("formeo-controls-"))
        .forEach((key) => window.localStorage.removeItem(key));
};

const disableFormeoControlSorting = (editorInstance) => {
    const groups = editorInstance?.controls?.groups;
    const Sortable = typeof window !== "undefined" ? window.Sortable : null;
    if (!groups || !Sortable?.get) return;

    Array.from(groups).forEach((group) => {
        const sortable = Sortable.get(group);
        if (sortable) {
            sortable.option("sort", false);
        }
    });
};

const applyRequiredStateToFieldElement = (fieldEl, isRequired) => {
    if (!fieldEl) return;
    if (isRequired) {
        fieldEl.classList.add("required-field");
    } else {
        fieldEl.classList.remove("required-field");
    }
};

const isRequiredToggleEnhanced = (fieldEl) =>
    fieldEl?.dataset?.requiredEnhanced === "true";

const markRequiredToggleEnhanced = (fieldEl) => {
    if (fieldEl) fieldEl.dataset.requiredEnhanced = "true";
};

const getFieldFromElement = (fieldEl, editorInstance) => {
    const fieldId = getFieldIdFromElement(fieldEl);
    if (fieldId && editorInstance?.formData?.fields?.[fieldId]) {
        return editorInstance.formData.fields[fieldId];
    }

    const nameEl = fieldEl.querySelector(".field-preview [name]");
    const name = nameEl?.getAttribute("name");
    if (!name) return null;

    return Object.values(editorInstance?.formData?.fields || {}).find(
        (f) => f?.config?.sys?.full_name === name || f?.attrs?.name === name
    );
};

const isHeaderField = (field) => {
    const { type, tag } = getFieldTypeTag(field);
    return type === "header" || HEADER_TAG_OPTIONS.includes(tag);
};

const parseInlineStyle = (styleString) => {
    if (!styleString || typeof styleString !== "string") return {};
    return styleString
        .split(";")
        .map((chunk) => chunk.trim())
        .filter(Boolean)
        .reduce((acc, declaration) => {
            const [prop, ...rest] = declaration.split(":");
            if (!prop || !rest.length) return acc;
            acc[prop.trim().toLowerCase()] = rest.join(":").trim();
            return acc;
        }, {});
};

const toInlineStyleString = (styleMap) => {
    return Object.entries(styleMap)
        .filter(([, value]) => typeof value === "string" && value.trim())
        .map(([key, value]) => `${key}: ${value}`)
        .join("; ");
};

const getHeaderStyleState = (field) => {
    const tag = typeof field?.tag === "string" ? field.tag.toLowerCase() : "";
    const styleMap = parseInlineStyle(field?.attrs?.style);
    const rawSize = (styleMap["font-size"] || "").replace("px", "").trim();
    const fontSize = rawSize && !Number.isNaN(Number(rawSize)) ? rawSize : "";
    const fontWeight = styleMap["font-weight"] || "700";

    return {
        headingTag: HEADER_TAG_OPTIONS.includes(tag) ? tag : "h1",
        fontSize,
        fontWeight,
    };
};

const applyHeaderStyleState = (field, { headingTag, fontSize, fontWeight }) => {
    if (!field) return;
    field.attrs ??= {};

    if (headingTag && HEADER_TAG_OPTIONS.includes(headingTag)) {
        field.tag = headingTag;
    }

    const styleMap = parseInlineStyle(field.attrs.style);
    if (fontSize) {
        styleMap["font-size"] = `${fontSize}px`;
    } else {
        delete styleMap["font-size"];
    }

    if (fontWeight) {
        styleMap["font-weight"] = fontWeight;
    } else {
        delete styleMap["font-weight"];
    }

    const nextStyle = toInlineStyleString(styleMap);
    if (nextStyle) {
        field.attrs.style = nextStyle;
    } else {
        delete field.attrs.style;
    }
};

const updateHeaderPreviewNode = (fieldEl, headerState) => {
    if (!fieldEl) return;
    const preview = fieldEl.querySelector(".field-preview");
    if (!preview) return;

    let headingEl = preview.querySelector("h1, h2, h3, h4, h5, h6");
    if (!headingEl) return;

    const nextTag = headerState.headingTag;
    if (nextTag && headingEl.tagName.toLowerCase() !== nextTag) {
        const replacement = document.createElement(nextTag);
        replacement.innerHTML = headingEl.innerHTML;
        replacement.className = headingEl.className;
        Array.from(headingEl.attributes).forEach((attr) => {
            if (attr.name === "class" || attr.name === "style") return;
            replacement.setAttribute(attr.name, attr.value);
        });
        if (headingEl.parentNode) {
            headingEl.parentNode.replaceChild(replacement, headingEl);
            headingEl = replacement;
        }
    }

    headingEl.style.fontSize = headerState.fontSize ? `${headerState.fontSize}px` : "";
    headingEl.style.fontWeight = headerState.fontWeight || "";
};

const isHeaderStyleEnhanced = (fieldEl) =>
    fieldEl?.dataset?.headerStyleEnhanced === "true";

const markHeaderStyleEnhanced = (fieldEl) => {
    if (fieldEl) fieldEl.dataset.headerStyleEnhanced = "true";
};

// The schema-level swap in sanitizeFormeoSchema only runs on load/save/preview, so
// without this the builder would still show the filler copy until the form was saved.
const replaceDefaultParagraphContent = (editorRoot, editorInstance) => {
    if (!editorRoot || !editorInstance?.formData?.fields) return;

    editorRoot.querySelectorAll(".formeo-field").forEach((fieldEl) => {
        if (fieldEl.closest(".formeo-controls")) return;

        const preview = fieldEl.querySelector(".field-preview");
        const paragraphEl = preview?.querySelector("p");
        if (!paragraphEl || !isDefaultParagraphContent(paragraphEl.innerHTML)) return;

        paragraphEl.textContent = DEFAULT_PARAGRAPH_TEXT;

        // `content` is a plain string, so it's copied by value into the object
        // formData hands back — writing to that copy would be discarded. Go through
        // the live component instead.
        const fieldId = getFieldIdFromElement(fieldEl);
        const component = fieldId ? editorInstance?.Components?.fields?.get(fieldId) : null;
        component?.setData?.("content", DEFAULT_PARAGRAPH_TEXT);
    });
};

const enhanceHeaderStyleControls = (editorRoot, editorInstance) => {
    if (!editorRoot || !editorInstance?.formData?.fields) return;
    const fields = editorRoot.querySelectorAll(".formeo-field");

    fields.forEach((fieldEl) => {
        if (fieldEl.closest(".formeo-controls")) return;
        if (isHeaderStyleEnhanced(fieldEl)) return;

        const field = getFieldFromElement(fieldEl, editorInstance);
        if (!field || !isHeaderField(field)) return;

        const controlsWrap = document.createElement("div");
        controlsWrap.className = "heading-editor mt-2 flex items-center gap-2 flex-wrap rounded-md border border-gray-200 bg-light px-2 py-2";
        controlsWrap.dataset.headerStyleControls = "true";

        const headingGroup = document.createElement("div");
        headingGroup.className = "inline-flex items-center gap-1 rounded border border-gray-300 bg-card p-1 !hidden";

        const weightGroup = document.createElement("div");
        weightGroup.className = "inline-flex items-center gap-1 rounded border border-gray-300 bg-card p-1";

        const sizeInput = document.createElement("input");
        sizeInput.type = "text";
        sizeInput.inputMode = "numeric";
        sizeInput.pattern = "[0-9]*";
        sizeInput.className = "input input-sm !w-12 text-center";
        sizeInput.placeholder = "16";
        sizeInput.setAttribute("aria-label", "Font size");

        const sizeWrap = document.createElement("div");
        sizeWrap.className = "inline-flex items-center rounded bg-light gap-1";
        const pxLabel = document.createElement("span");
        pxLabel.className = "text-xs text-gray-500 pr-1";
        pxLabel.textContent = "px";
        sizeWrap.append(sizeInput, pxLabel);

        const headingButtons = new Map();
        const weightButtons = new Map();

        // "Normal" must be an explicit 400, not an empty value. An empty value made
        // applyHeaderStyleState *delete* the declaration, and a heading with no
        // font-weight then gets 700 put back by sanitizeFormeoSchema (and inherits
        // bold from the row wrapper when rendered) — so picking Normal produced a
        // bold heading.
        const weightOptions = [
            { value: "400", label: "Normal" },
            { value: "500", label: "Medium" },
            { value: "600", label: "Semi" },
            { value: "700", label: "Bold" },
            // { value: "800", label: "XBold" },
        ];

        const setToolbarButtonState = (buttonsMap, activeValue) => {
            buttonsMap.forEach((btn, value) => {
                btn.className = value === activeValue
                    ? "btn btn-sm !btn-primary !border-primary btn-l h-7 min-h-0 px-2"
                    : "btn btn-sm !btn-light btn-outline h-7 min-h-0 px-2";
            });
        };

        let headingValue = "h1";
        let weightValue = "";

        HEADER_TAG_OPTIONS.forEach((tag) => {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.textContent = tag.toUpperCase();
            btn.setAttribute("aria-label", `Set ${tag.toUpperCase()} heading`);
            btn.addEventListener("click", () => {
                headingValue = tag;
                applyFromControls({ clampMin: true });
            });
            headingButtons.set(tag, btn);
            headingGroup.appendChild(btn);
        });

        weightOptions.forEach((item) => {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.textContent = item.label;
            btn.setAttribute("aria-label", `Set ${item.label} weight`);
            btn.addEventListener("click", () => {
                weightValue = item.value;
                applyFromControls({ clampMin: true });
            });
            weightButtons.set(item.value, btn);
            weightGroup.appendChild(btn);
        });

        const syncFromField = () => {
            const headerState = getHeaderStyleState(field);
            headingValue = headerState.headingTag;
            weightValue = headerState.fontWeight;
            sizeInput.value = headerState.fontSize;
            setToolbarButtonState(headingButtons, headingValue);
            setToolbarButtonState(weightButtons, weightValue);
            updateHeaderPreviewNode(fieldEl, headerState);
        };

        const applyFromControls = ({ clampMin = false } = {}) => {
            let sanitizedSize = "";
            const rawValue = (sizeInput.value || "").replace(/[^\d]/g, "");
            if (sizeInput.value !== rawValue) {
                sizeInput.value = rawValue;
            }
            if (rawValue) {
                const numeric = Number(rawValue);
                if (!Number.isNaN(numeric)) {
                    sanitizedSize = String(clampMin ? Math.max(8, numeric) : numeric);
                }
            }
            if (clampMin && sizeInput.value && sanitizedSize !== sizeInput.value) {
                sizeInput.value = sanitizedSize;
            }
            const headerState = {
                headingTag: headingValue,
                fontSize: sanitizedSize,
                fontWeight: weightValue,
            };
            applyHeaderStyleState(field, headerState);
            updateHeaderPreviewNode(fieldEl, headerState);
            setToolbarButtonState(headingButtons, headingValue);
            setToolbarButtonState(weightButtons, weightValue);
        };

        sizeInput.addEventListener("focus", () => sizeInput.select());
        sizeInput.addEventListener("input", () => applyFromControls({ clampMin: false }));
        sizeInput.addEventListener("blur", () => applyFromControls({ clampMin: true }));

        // These controls live inside a .formeo-field, which Formeo makes draggable and
        // treats as a single selectable unit. Its pointer handling swallows the
        // mousedown that would focus the size box, so the field looked unresponsive —
        // the weight buttons still worked because a click alone is enough for them.
        // Every other control injected into a field in this file guards its events the
        // same way.
        const stopBubbling = (event) => event.stopPropagation();
        ['pointerdown', 'mousedown', 'touchstart', 'click', 'keydown'].forEach((eventName) => {
            controlsWrap.addEventListener(eventName, stopBubbling);
        });
        controlsWrap.draggable = false;

        controlsWrap.append(headingGroup, sizeWrap, weightGroup);
        const previewEl = fieldEl.querySelector(".field-preview");
        if (previewEl && previewEl.parentNode === fieldEl) {
            previewEl.insertAdjacentElement("beforebegin", controlsWrap);
        } else {
            fieldEl.appendChild(controlsWrap);
        }

        markHeaderStyleEnhanced(fieldEl);
        syncFromField();
    });
};

const enhanceRequiredToggleControls = (editorRoot, editorInstance) => {
    if (!editorRoot || !editorInstance?.formData?.fields) return;

    const fields = editorRoot.querySelectorAll(".formeo-field");

    fields.forEach((fieldEl) => {
        if (fieldEl.closest(".formeo-controls")) return;
        if (isRequiredToggleEnhanced(fieldEl)) return;

        const field = getFieldFromElement(fieldEl, editorInstance);
        if (!field) return;
        if (!isEligibleForRequiredToggle(field)) return;

        const locked = isLockedRequiredField(field);

        const toggleBtn = document.createElement("button");
        toggleBtn.type = "button";
        toggleBtn.dataset.toggleRequired = "true";
        toggleBtn.classList.add("required-toggle-btn");

        const sync = () => {
            const required = locked ? true : isRequiredField(field);
            setFieldRequired(field, required);
            updateControlPanelRequiredIndicator(field, required);
            updatePreviewRequiredIndicator(fieldEl, required);
            disablePreviewInputs(fieldEl, field);
            toggleBtn.className = required
                ? "mt-1 text-xs !btn !btn-link-danger mr-4"
                : "mt-1 text-xs !btn !btn-link mr-4";
            toggleBtn.textContent = locked
                ? "Required (Locked)"
                : required
                    ? "Mark Optional"
                    : "Mark Required";

            toggleBtn.disabled = locked;
            syncFieldActionControlsVisibility(fieldEl);
        };

        toggleBtn.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (locked) return;
            const nextRequired = !isRequiredField(field);
            setFieldRequired(field, nextRequired);
            sync();
        };

        const controlsWrap = getFieldActionControlsWrap(fieldEl);
        controlsWrap?.appendChild(toggleBtn);
        markRequiredToggleEnhanced(fieldEl);
        sync();
        syncFieldActionControlsVisibility(fieldEl);
    });
};

const getRequiredSystemFieldNames = (controls) => {
    return controls
        .filter((control) => control?.attrs?.required && control?.config?.sys?.full_name)
        .map((control) => control.config.sys.full_name);
};

const getSchemaFieldNames = (schema) => {
    if (!schema?.fields) return new Set();
    return new Set(
        Object.values(schema.fields)
            .map((field) => field?.config?.sys?.full_name || field?.attrs?.name)
            .filter(Boolean)
    );
};

const enhanceRowDuplicateControls = (editorRoot, onDuplicate) => {
    if (!editorRoot) return;

    editorRoot.querySelectorAll(".formeo-row").forEach((rowEl) => {
        if (rowEl.closest(".formeo-controls")) return;
        if (rowEl.dataset.rowDuplicateEnhanced === "true") return;

        const actionWrap = rowEl.querySelector(":scope > .row-actions .row-action-btn-wrap");
        if (!actionWrap) return;

        // Formeo ships its own clone button, hidden in this builder because its deep
        // clone would copy system fields too. Reusing its markup keeps the icon and
        // styling identical to the move and remove buttons beside it; the CSS
        // un-hides it again once the marker class is on it.
        const template = actionWrap.querySelector(".item-clone:not(.row-duplicate-btn)");
        if (!template) return;

        const button = template.cloneNode(true);
        button.type = "button";
        button.classList.add("row-duplicate-btn");
        button.title = "Duplicate row";
        button.setAttribute("aria-label", "Duplicate row");
        button.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            onDuplicate(rowEl.id);
        });

        const removeBtn = actionWrap.querySelector(".item-remove");
        if (removeBtn) {
            removeBtn.insertAdjacentElement("beforebegin", button);
        } else {
            actionWrap.appendChild(button);
        }

        rowEl.dataset.rowDuplicateEnhanced = "true";
    });
};

const getMissingRequiredSystemFields = (controls, schema) => {
    const requiredFields = controls.filter(
        (control) => control?.config?.sys?.full_name && isRequiredControl(control)
    );
    if (!requiredFields.length) return [];

    const schemaFieldNames = getSchemaFieldNames(schema);

    return requiredFields
        .filter((control) => !schemaFieldNames.has(control.config.sys.full_name))
        .map((control) => ({
            name: control.config.sys.full_name,
            label: control.config.label || control.config.sys.full_name,
        }));
};

const ensureRepeatableHtmlControls = () => {
    const controls = document.querySelectorAll(".formeo-controls .field-control");
    controls.forEach((control) => {
        const classList = Array.from(control.classList);
        const isSystemControl = classList.some(
            (cls) => cls.startsWith("sys_") && cls.endsWith("-control")
        );
        if (isSystemControl) return;

        // Keep non-system controls reusable even after drag/drop interaction.
        control.classList.remove("disabled", "is-disabled", "sys-control-disabled", "inactive");
        control.removeAttribute("disabled");
        control.setAttribute("aria-disabled", "false");
        control.style.pointerEvents = "";
        control.style.opacity = "";

        const controlItem = control.closest("li, .control-item, .field-control-wrap");
        if (controlItem) {
            controlItem.classList.remove("disabled", "is-disabled", "inactive");
            controlItem.removeAttribute("disabled");
            controlItem.setAttribute("aria-disabled", "false");
            controlItem.style.pointerEvents = "";
            controlItem.style.opacity = "";
        }

        const nestedDisabled = control.querySelectorAll("[disabled], .disabled, .is-disabled, .inactive");
        nestedDisabled.forEach((node) => {
            if (node instanceof HTMLElement) {
                node.classList.remove("disabled", "is-disabled", "inactive");
                node.removeAttribute("disabled");
                node.setAttribute("aria-disabled", "false");
                node.style.pointerEvents = "";
                node.style.opacity = "";
            }
        });
    });
};

const bindNonSystemControlClickFallback = (editorRoot, editorInstance) => {
    if (!editorRoot || !editorInstance?.controls?.addElement) return;
    const controlsRoot = editorRoot.querySelector(".formeo-controls");
    if (!controlsRoot) return;
    if (controlsRoot.dataset.nonSystemClickBound === "true") return;

    controlsRoot.addEventListener(
        "click",
        (event) => {
            const btn = event.target.closest(".field-control > button");
            if (!btn) return;

            const control = btn.closest(".field-control");
            if (!control) return;

            const classList = Array.from(control.classList);
            const isSystemControl = classList.some(
                (cls) => cls.startsWith("sys_") && cls.endsWith("-control")
            );
            if (isSystemControl) return;

            const controlId = control.id;
            if (!controlId) return;

            // Fallback add for native controls, especially after drag/drop.
            event.preventDefault();
            event.stopPropagation();
            editorInstance.controls.addElement(controlId);
        },
        true
    );

    controlsRoot.dataset.nonSystemClickBound = "true";
};

const getControlSearchText = (control) => {
    const btn = control.querySelector("button");
    return (btn?.textContent || control.textContent || "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();
};

const applyControlsSearchFilter = (controlsRoot, query = "") => {
    if (!controlsRoot) return;
    const normalizedQuery = query.toLowerCase().trim();

    const controls = controlsRoot.querySelectorAll(".field-control");
    controls.forEach((control) => {
        const searchText = getControlSearchText(control);
        const isMatch = !normalizedQuery || searchText.includes(normalizedQuery);
        const wrapper = control.closest("li, .control-item, .field-control-wrap") || control;
        wrapper.style.display = isMatch ? "" : "none";
    });
};

const ensureControlSearch = (editorRoot) => {
    if (!editorRoot) return;

    const controlsRoot = editorRoot.querySelector(".formeo-controls");
    if (!controlsRoot) return;

    let input = controlsRoot.querySelector('input[data-formeo-control-search="true"]');

    if (!input) {
        const searchWrap = document.createElement("div");
        searchWrap.className = "pb-2";

        input = document.createElement("input");
        input.type = "text";
        input.className = "!input !input-sm w-full";
        input.placeholder = "Search fields...";
        input.setAttribute("data-formeo-control-search", "true");

        input.addEventListener("input", (event) => {
            const query = event.target.value || "";
            controlsRoot.dataset.searchQuery = query;
            applyControlsSearchFilter(controlsRoot, query);
        });

        searchWrap.appendChild(input);
        controlsRoot.prepend(searchWrap);
    }

    const persistedQuery = controlsRoot.dataset.searchQuery || "";
    if (input.value !== persistedQuery) {
        input.value = persistedQuery;
    }
    applyControlsSearchFilter(controlsRoot, persistedQuery);
};

const getSystemControlFullName = (control) => {
    const sysClass = Array.from(control.classList).find(
        (cls) => cls.startsWith("sys_") && cls.endsWith("-control")
    );
    if (!sysClass) return null;

    const raw = sysClass.replace("sys_", "").replace("-control", "");
    const firstUnderscoreIndex = raw.indexOf("_");
    if (firstUnderscoreIndex === -1) return null;

    return (
        raw.slice(0, firstUnderscoreIndex) +
        "." +
        raw.slice(firstUnderscoreIndex + 1)
    );
};

const decorateCustomFieldBadges = (editorRoot, customFieldNames) => {
    if (!editorRoot) return;

    const controls = editorRoot.querySelectorAll(".formeo-controls .field-control[class*='sys_']");
    controls.forEach((control) => {
        const fullName = getSystemControlFullName(control);
        if (!fullName) return;

        const button = control.querySelector("button");
        if (!button) return;

        const existingBadge = button.querySelector('[data-custom-field-badge="true"]');
        const isCustom = customFieldNames?.has(fullName);
        control.classList.toggle("custom-field-control", Boolean(isCustom));
        button.classList.toggle("custom-field-control-btn", Boolean(isCustom));
        control.setAttribute("data-custom-field", isCustom ? "true" : "false");

        if (isCustom && !existingBadge) {
            const badge = document.createElement("span");
            badge.setAttribute("data-custom-field-badge", "true");
            badge.className = "custom-field-badge ml-2 mb-2 badge badge-pill badge-primary badge-outline badge-xs";
            badge.textContent = "Custom";
            button.appendChild(badge);
        }

        if (!isCustom && existingBadge) {
            existingBadge.remove();
        }
    });
};

const CreateIntakeForm = () => {
    const editorRef = useRef(null);
    const editorInitRef = useRef(false);
    const formLoadedRef = useRef(false);
    const checkboxOtherRef = useRef(createCheckboxOtherState());
    const pendingSchemaRef = useRef(null);
    const customFieldNamesRef = useRef(new Set());
    const previewContainerRef = useRef(null);
    const builderBaselineRef = useRef(null);
    const duplicateRowRef = useRef(null);

    const [sysControls, setSysControls] = useState([]);
    const [fetchedForm, setFetchedForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [isFormeoInitializing, setIsFormeoInitializing] = useState(true);
    const [areControlsReady, setAreControlsReady] = useState(false);
    const [isFormLoaded, setIsFormLoaded] = useState(false);
    const [isEditorReady, setIsEditorReady] = useState(false);
    const [isCustomFieldModalOpen, setIsCustomFieldModalOpen] = useState(false);
    const [createFieldLoading, setCreateFieldLoading] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    const { id } = useParams();
    const navigate = useNavigate();
    const mode = id ? "edit" : "create";

    // Recipients are free text, so nothing supplies them as options. Feed the
    // saved addresses back in or the chips render empty when editing a form.
    const recipientOptions = useMemo(
        () => (fetchedForm?.submission_recipients || []).map((email) => ({
            label: email,
            value: email,
        })),
        [fetchedForm]
    );

    useEffect(() => {
        customFieldNamesRef.current = new Set(
            (sysControls || [])
                .filter((control) =>
                    String(control?.config?.category || "").startsWith("contact.custom") ||
                    String(control?.config?.category || "").startsWith("matter.custom")
                )
                .map((control) => control?.config?.sys?.full_name)
                .filter(Boolean)
        );
    }, [sysControls]);

    const fetchSystemFields = async () => {
        try {
            const { data, status } = await intakeFormService.getContactMatterSystemFields();
            if (status) {
                setSysControls(mapSystemFieldsToFormeoControls(data));
                setAreControlsReady(true);
            }
        } catch (error) {
            console.error('Error fetching system fields:', error);
            console.error('Oops! Something went wrong.', {
                description: 'An error occurred while fetching system fields.',
            });
        }
    };

    // Fetch system fields
    useEffect(() => {
        fetchSystemFields();
    }, []);

    const handleSubmitCustomField = async (formData, formikHelpers) => {
        setCreateFieldLoading(true);
        try {
            const { status } = await customFieldService.createCustomField(formData);
            if (status) {
                toast.success('Custom Field Created Successfully.');
                setIsCustomFieldModalOpen(false);

                if (editorRef.current?.formData) {
                    pendingSchemaRef.current = sanitizeFormeoSchema(editorRef.current.formData);
                }

                if (editorRef.current?.destroy) {
                    editorRef.current.destroy();
                }
                editorRef.current = null;
                editorInitRef.current = false;
                setIsEditorReady(false);
                setIsFormeoInitializing(true);

                await fetchSystemFields();
            }
        } catch (error) {
            console.error('Error', error);
            if (error.message == '422 Unprocessable Entity. Validation failed.') {
                const validationErrors = error.errors
                const formattedErrors = Object.fromEntries(
                    Object.entries(validationErrors).map(([key, value]) => [key, value[0]])
                );

                formikHelpers.setErrors(formattedErrors);
                toast.error('Validation Error.', {
                    description: 'Please check your input. Some fields may be invalid.',

                });
            }
            toast.error('Failed to create Custom Field.', {
                description: 'An error occurred while creating Custom Field. Please try again later.',
            });
        } finally {
            setCreateFieldLoading(false);
        }
    };

    // Fetch form
    useEffect(() => {
        if (!id) return;
        const fetchIntakeForm = async () => {
            try {
                const { data, status } = await intakeFormService.getSingleForm(id);
                if (status) {
                    console.log(JSON.parse(data.fields))
                    setFetchedForm(data);
                }
            } catch (error) {
                console.error('Error Fetching Intake Form', error)
            }
        }
        fetchIntakeForm();
    }, [id]);

    useEffect(() => {
        if (!id) return;
        formLoadedRef.current = false;
        setIsFormLoaded(false);
    }, [id]);

    const syncSysPanelControls = () => {
        const schema = editorRef.current?.formData;
        const usedSysFields = new Set(
            Object.values(schema?.fields || {})
                .map(f => f?.config?.sys?.full_name)
                .filter(Boolean)
        );

        document
            .querySelectorAll(".field-control[class*='sys_']")
            .forEach(control => {
                const normalized = getSystemControlFullName(control);
                if (!normalized) return;

                const isUsed = usedSysFields.has(normalized);

                if (isUsed) {
                    control.classList.add("sys-control-disabled");
                    control.setAttribute("aria-disabled", "true");
                } else {
                    control.classList.remove("sys-control-disabled");
                    control.removeAttribute("aria-disabled");
                }
            });

        decorateCustomFieldBadges(
            document.getElementById("formeo-editor"),
            customFieldNamesRef.current
        );
    };

    const runEditorEnhancements = (placeholderMap) => {
        const editorRoot = document.getElementById("formeo-editor");
        syncSysPanelControls();
        enhanceCheckboxOtherControls(editorRoot, checkboxOtherRef.current, editorRef.current);
        enhanceRequiredToggleControls(editorRoot, editorRef.current);
        replaceDefaultParagraphContent(editorRoot, editorRef.current);
        enhanceHeaderStyleControls(editorRoot, editorRef.current);
        enhanceSelectPlaceholders(editorRoot);
        enhanceMultiSelects(editorRoot, { placeholderMap });
        enhanceCheckboxPreviewLayout(editorRoot);
        enhanceRowDuplicateControls(editorRoot, (rowId) => duplicateRowRef.current?.(rowId));
    };

    // The builder holds the part of the form that matters most, and it lives
    // outside Formik, so the unsaved-changes prompt compares the current schema
    // against the one the page started with.
    const getBuilderSignature = () => {
        const schema = editorRef.current?.formData;
        if (!schema) return null;

        return stableStringify({
            stages: Object.values(schema.stages || {}).map((stage) => stage.children || []),
            rows: schema.rows || {},
            columns: schema.columns || {},
            fields: schema.fields || {},
        });
    };

    // Only a real reading becomes the baseline. Storing "" for a schema we could
    // not read would make every later comparison report changes, so an
    // unreadable editor leaves the baseline unset and a later attempt retries.
    const captureBuilderBaseline = () => {
        if (builderBaselineRef.current !== null) return;
        const signature = getBuilderSignature();
        if (signature === null) return;
        builderBaselineRef.current = signature;
    };

    const hasBuilderChanges = () => {
        if (builderBaselineRef.current === null) return false;
        const signature = getBuilderSignature();
        return signature !== null && signature !== builderBaselineRef.current;
    };

    const handleDuplicateRow = (rowId) => {
        const editor = editorRef.current;
        if (!editor?.formData) return;

        let schema;
        try {
            schema = JSON.parse(JSON.stringify(editor.formData));
        } catch (error) {
            console.error("Could not read the form builder schema:", error);
            toast.error("Could not duplicate this row.");
            return;
        }

        const result = duplicateRowInSchema(schema, rowId);
        if (!result) {
            toast.error("Could not duplicate this row.");
            return;
        }

        schema = sanitizeFormeoSchema(schema);
        schema = disableSystemFieldsForBuilder(schema);
        schema = enforceLockedRequiredFields(schema);

        const placeholderMap = buildFieldPlaceholderMap(editor.formData);
        editor.load(schema);
        requestAnimationFrame(() => runEditorEnhancements(placeholderMap));

        if (result.skippedNamedFields) {
            const wording =
                result.skippedNamedFields === 1
                    ? "1 field was"
                    : `${result.skippedNamedFields} fields were`;
            toast.info("Row layout duplicated.", {
                description: `${wording} left out, because each can only be used once per form. Drag the fields you need into the new row.`,
            });
            return;
        }

        toast.success("Row duplicated.");
    };

    // Kept in a ref so the row buttons, which are plain DOM nodes rebuilt by the
    // MutationObserver, always reach the current handler. Assigned in an effect
    // rather than during render: startTransition can discard a render, and a
    // discarded one must not leave its closure behind.
    useEffect(() => {
        duplicateRowRef.current = handleDuplicateRow;
    });

    // Init Formeo
    useEffect(() => {
        if (!sysControls.length || editorInitRef.current) return;

        editorInitRef.current = true;
        setIsFormeoInitializing(true)
        clearFormeoControlOrder();
        editorRef.current = new FormeoEditor({
            editorContainer: "#formeo-editor",
            defaultControls: [],
            controls: {
                ...buildControlOptions(sysControls),
                sortable: false,
            },
            onLoad: () => {
                setIsFormeoInitializing(false);
                setIsEditorReady(true);
                clearFormeoControlOrder();
                disableFormeoControlSorting(editorRef.current);
                ensureRepeatableHtmlControls();
                bindNonSystemControlClickFallback(document.getElementById("formeo-editor"), editorRef.current);
                ensureControlSearch(document.getElementById("formeo-editor"));
            },
        });

    }, [sysControls]);

    useEffect(() => {
        if (!isEditorReady || !pendingSchemaRef.current) return;
        let schema = pendingSchemaRef.current;
        schema = disableSystemFieldsForBuilder(schema);
        schema = enforceLockedRequiredFields(schema);
        checkboxOtherRef.current = getCheckboxOtherStateFromSchema(schema);
        pendingSchemaRef.current = null;

        requestAnimationFrame(() => {
            const placeholderMap = buildFieldPlaceholderMap(editorRef.current?.formData);
            editorRef.current?.load(schema);
            runEditorEnhancements(placeholderMap);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditorReady]);


    useEffect(() => {
        const editorRoot = document.getElementById("formeo-editor");
        if (!editorRoot) return;

        // 1 Observe DOM changes (add, move)
        const handleDomChange = () => {
            const placeholderMap = buildFieldPlaceholderMap(editorRef.current?.formData);
            syncSysPanelControls();
            ensureRepeatableHtmlControls();
            bindNonSystemControlClickFallback(editorRoot, editorRef.current);
            ensureControlSearch(editorRoot);
            enhanceCheckboxOtherControls(
                editorRoot,
                checkboxOtherRef.current,
                editorRef.current
            );
            enhanceRequiredToggleControls(editorRoot, editorRef.current);
            replaceDefaultParagraphContent(editorRoot, editorRef.current);
            enhanceHeaderStyleControls(editorRoot, editorRef.current);
            enhanceSelectPlaceholders(editorRoot);
            enhanceMultiSelects(editorRoot, { placeholderMap });
            enhanceCheckboxPreviewLayout(editorRoot);
            enhanceRowDuplicateControls(editorRoot, (rowId) => duplicateRowRef.current?.(rowId));
        };

        const observer = new MutationObserver(handleDomChange);

        observer.observe(editorRoot, {
            childList: true,
            subtree: true,
        });

        // 2️ Explicitly handle remove clicks
        const handleRemoveClick = (e) => {
            const removeBtn = e.target.closest(".item-remove");
            if (!removeBtn) return;

            const fieldEl = removeBtn.closest(".formeo-field");
            if (fieldEl) {
                const fieldId = getFieldIdFromElement(fieldEl);
                const field = fieldId
                    ? editorRef.current?.formData?.fields?.[fieldId]
                    : null;

                if (field && isLockedRequiredField(field)) {
                    e.preventDefault();
                    e.stopPropagation();
                    toast.warning("This field is required and cannot be removed.");
                    return;
                }
            }

            // wait for Formeo to finish DOM cleanup
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    syncSysPanelControls();
                });
            });
        };

        editorRoot.addEventListener("click", handleRemoveClick);

        // initial sync
        handleDomChange();

        return () => {
            observer.disconnect();
            editorRoot.removeEventListener("click", handleRemoveClick);
        };
    }, []);

    /* Load form */
    useEffect(() => {
        if (!isEditorReady || !areControlsReady || !fetchedForm?.fields || formLoadedRef.current) return;

        let schema;
        try {
            schema = sanitizeFormeoSchema(JSON.parse(fetchedForm.fields));
        } catch (error) {
            console.error("Invalid form schema JSON:", error);
            setIsFormLoaded(true);
            return;
        }

        //  THIS IS THE KEY
        schema = disableSystemFieldsForBuilder(schema);
        schema = enforceLockedRequiredFields(schema);
        checkboxOtherRef.current = getCheckboxOtherStateFromSchema(schema);
        formLoadedRef.current = true;

        requestAnimationFrame(() => {
            const placeholderMap = buildFieldPlaceholderMap(editorRef.current?.formData);
            editorRef.current?.load(schema);
            runEditorEnhancements(placeholderMap);
            setIsFormLoaded(true);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchedForm, isEditorReady, areControlsReady]);

    useEffect(() => {
        if (mode === "create") {
            setIsFormLoaded(true);
        }
    }, [mode]);

    // Whatever the builder holds once the page has finished loading is the
    // baseline, so unsaved-changes detection works the same on a new form, a
    // saved one, and a saved one whose schema failed to parse. Keyed on the flag
    // every one of those paths sets, rather than on any single happy path.
    useEffect(() => {
        if (!isEditorReady || !isFormLoaded) return;
        captureBuilderBaseline();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditorReady, isFormLoaded]);

    const showLoader =
        isFormeoInitializing ||
        !areControlsReady ||
        (mode === "edit" && !isFormLoaded);

    const renderFormPreview = useCallback(() => {
        const container = previewContainerRef.current;
        if (!container) return;

        container.innerHTML = "";
        const sourceSchema = editorRef.current?.formData;
        if (!sourceSchema?.fields || !Object.keys(sourceSchema.fields).length) return;

        let schema;
        try {
            schema = JSON.parse(JSON.stringify(sourceSchema));
        } catch {
            schema = sourceSchema;
        }

        schema = sanitizeFormeoSchema(schema);
        schema = enforceLockedRequiredFields(schema);
        schema = applyCheckboxOtherToSchema(schema, checkboxOtherRef.current);
        schema = stripDisabledFromSchema(schema);

        const renderer = new FormeoRenderer({
            renderContainer: container,
        });

        renderer.render(schema);
        requestAnimationFrame(() => {
            const placeholderMap = buildFieldPlaceholderMap(editorRef.current?.formData);
            injectCheckboxOtherOptions(container, schema);
            enhanceRenderedSingleCheckboxPreview(container);
            applyCheckboxRequiredAttributes(container, schema);
            applyInputKinds(container, getInputKinds(schema));
            enhanceSelectPlaceholders(container, {
                selector: "select:not([multiple])",
            });
            enhanceMultiSelects(container, {
                selector: "select[multiple]",
                isDisabled: false,
                placeholderMap,
            });
            enhancePreviewCityStateFields(container);
        });
    }, []);

    useEffect(() => {
        if (!showPreview || !isEditorReady) return;

        renderFormPreview();
        const editorRoot = document.getElementById("formeo-editor");
        if (!editorRoot) return;

        let rafId = null;
        const schedulePreviewRender = () => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                renderFormPreview();
            });
        };

        const observer = new MutationObserver(schedulePreviewRender);
        observer.observe(editorRoot, {
            childList: true,
            subtree: true,
            attributes: true,
            characterData: true,
        });

        return () => {
            observer.disconnect();
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [showPreview, isEditorReady, renderFormPreview]);

    const handleSubmit = async (values, { setErrors }) => {
        const data = editorRef.current?.formData;
        if (!data?.fields || !Object.keys(data.fields).length) {
            toast.warning("All required fields from Contact and Matter must be filled to create the form.");
            return;
        }

        const missingRequired = getMissingRequiredSystemFields(sysControls, data);
        if (missingRequired.length) {
            toast.warning("Please add all required contact/matter fields before saving.", {
                description: missingRequired.map((field) => field.label).join(", "),
            });
            return;
        }

        setSaving(true);
        try {
            let schema = sanitizeFormeoSchema(editorRef.current.formData);
            schema = enforceLockedRequiredFields(schema);
            schema = applyCheckboxOtherToSchema(schema, checkboxOtherRef.current);

            const payload = {
                ...values,
                fields: stripDisabledFromSchema(schema),
            };
            const action =
                mode === "edit"
                    ? intakeFormService.updateIntakeForm
                    : intakeFormService.createIntakeForm;

            await action(mode === "edit" ? id : payload, payload);
            toast.success(`Form ${mode === "edit" ? "updated" : "created"}`);
            navigate("/intake-forms");
        } catch (error) {
            console.error("Error submitting form:", error);
            if (error.message === "422 Unprocessable Entity. Validation failed.") {
                const formatted = Object.fromEntries(
                    Object.entries(error.errors || {}).map(([key, val]) => [key, val[0]])
                );

                setErrors(formatted);

                return;
            }
            toast.error("Oops! Something went wrong.", {
                description: "An error occurred while saving the form.",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="card formeo-builder">
            <div className="card-header">
                <h3>{mode === "edit" ? "Edit" : "Create"} Intake Form</h3>
                <div className="flex flex-wrap items-center gap-2">
                    {/* A new tab, so the form being built here isn't lost. */}
                    <Link
                        to="/account/home/custom-fields"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-light"
                    >
                        <KeenIcon icon="setting-2" />
                        Manage Custom Fields
                    </Link>
                    <button
                        type="button"
                        className="btn btn-sm btn-primary btn-outline"
                        onClick={() => setIsCustomFieldModalOpen(true)}
                    >
                        <KeenIcon icon="plus" />
                        Create Custom Field
                    </button>
                </div>
            </div>

            <Formik
                enableReinitialize
                initialValues={{
                    name: fetchedForm?.name || "",
                    status: fetchedForm?.status || "active",
                    submit_button_label: fetchedForm?.submit_button_label || "Submit Form",
                    confirmation_message: fetchedForm?.confirmation_message || "",
                    submission_recipients: fetchedForm?.submission_recipients || [],
                }}
                validationSchema={Yup.object({
                    name: Yup.string().required(),
                    status: Yup.string().required(),
                    submission_recipients: Yup.array()
                        .of(Yup.string().email(({ value }) => `"${value}" is not a valid email address`))
                        .max(20, "A form can notify at most 20 recipients"),
                    confirmation_message: Yup.string().max(1000, "Keep the message under 1,000 characters"),
                })}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, values, errors, touched }) => (
                    <>
                        <Form className="card-body">
                            <FormikUnsavedChangesGuard
                                saving={saving}
                                extraDirty={hasBuilderChanges}
                                saveLabel="Save form"
                            />
                            <div className="flex items-start w-full flex-wrap sm:flex-nowrap gap-2">
                                <div className="sm:w-1/3 w-full">
                                    <label className="form-label mb-1" htmlFor="name">Form Name <span className="text-red-600 pl-px">*</span></label>
                                    <Field
                                        type="text"
                                        id="name"
                                        name="name"
                                        className="input"
                                        placeholder="Enter form name"
                                    />
                                    <ErrorMessage name="name" component="div" className="mt-1 text-xs text-red-500" />
                                </div>

                                <div className="sm:w-1/3 w-full">
                                    <label className="form-label mb-1" htmlFor="status">Status <span className="text-red-600 pl-px">*</span></label>
                                    <LazyFormikSelect
                                        name="status"
                                        placeholder='Select Form Status'
                                        options={[
                                            { value: "active", label: "Active" },
                                            { value: "inactive", label: "Inactive" }
                                        ]}
                                        preloadedOption={values.status}
                                    />
                                </div>

                                <div className="sm:w-1/3 w-full">
                                    <label className="form-label mb-1" htmlFor="submit_button_label">Submit Button Label</label>
                                    <Field
                                        type="text"
                                        id="submit_button_label"
                                        name="submit_button_label"
                                        className="input"
                                        placeholder="Submit Form"
                                    />
                                </div>
                            </div>

                            <div className="w-full mt-4">
                                <label className="form-label mb-1" htmlFor="submission_recipients">Submission Recipients</label>
                                <LazyFormikSelect
                                    name="submission_recipients"
                                    isMulti={true}
                                    creatable={true}
                                    options={[]}
                                    placeholder="Type an email address and press Enter"
                                    preloadedOption={recipientOptions}
                                />
                                <div className="mt-1 text-xs text-gray-500">
                                    Enter one or more email addresses to receive notifications when this form is submitted.
                                    Leave empty to notify the form&apos;s creator.
                                </div>
                                {errors.submission_recipients && touched.submission_recipients && (
                                    <div className="mt-1 text-xs text-red-500">
                                        {[...new Set(
                                            Array.isArray(errors.submission_recipients)
                                                ? errors.submission_recipients.filter(Boolean)
                                                : [errors.submission_recipients]
                                        )].join(", ")}
                                    </div>
                                )}
                            </div>

                            <div className="w-full mt-4">
                                <label className="form-label mb-1" htmlFor="confirmation_message">Confirmation Message</label>
                                <Field
                                    as="textarea"
                                    id="confirmation_message"
                                    name="confirmation_message"
                                    className="textarea h-[90px]"
                                    maxLength={1000}
                                    placeholder="Form submitted successfully"
                                />
                                <div className="mt-1 text-xs text-gray-500">
                                    Shown in place of the form after someone submits it. Leave empty to show &ldquo;Form submitted successfully&rdquo;.
                                </div>
                                <ErrorMessage name="confirmation_message" component="div" className="mt-1 text-xs text-red-500" />
                            </div>

                            <div className="relative">
                                {showLoader && (
                                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary-light rounded-md border border-dashed border-primary">
                                        <div className="flex flex-col items-center gap-2">
                                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-primary" />
                                            <span className="text-sm text-gray-500">
                                                Initializing form builder…
                                            </span>
                                        </div>
                                    </div>
                                )}

                                <div
                                    id="formeo-editor"
                                    className="formeo-builder mt-4"
                                    style={{ minHeight: 400 }}
                                />
                            </div>

                            <div className="flex items-center gap-2 mt-4">
                                <button
                                    type="submit"
                                    disabled={isSubmitting || saving}
                                    className="btn btn-primary"
                                >
                                    {saving ? "Saving..." : mode === "edit" ? "Update Form" : "Create Form"}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-outline"
                                    onClick={() => setShowPreview((prev) => !prev)}
                                >
                                    {showPreview ? "Hide Preview" : "Show Preview"}
                                </button>
                            </div>
                        </Form>
                        <div className="card mx-6 mt-8">
                            {showPreview && (
                                <div className="">
                                    <div className="card-header">
                                        <h4 className="card-title">Form Preview</h4>
                                    </div>
                                    <div className="card-body">
                                        <div ref={previewContainerRef} className="formeo-renderer-container" />
                                        <div className="flex justify-end mt-4">
                                            <button type="button" className="btn btn-primary" disabled>
                                                {values.submit_button_label || "Submit Form"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </Formik>
            <CustomFieldModal
                open={isCustomFieldModalOpen}
                onClose={() => setIsCustomFieldModalOpen(false)}
                mode="create"
                data={null}
                onSubmit={handleSubmitCustomField}
                createFieldLoading={createFieldLoading}
                updateFieldLoading={false}
                hideUserField
            />
        </div>
    );
};

export { CreateIntakeForm };
